import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { map, catchError, timeout } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { AuthService } from "./auth.service";
import {ErrorHandlerService} from './error-handler.service';
import { Application } from '../core/model/application';
import {apiLinks} from "../core/api-links/api-links";
import { CommonService } from './common.service';

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {
  getApplicationsUrl = `${environment.steamApiUrl}/applications`;
  getApplicationSignUrl = `${environment.apiUrl}/applicationSignature`;
  getDeviceModalsUrl = `${environment.apiUrl}/device-models-by-manufacturer/`;
  getLatestBuildNumber = `${environment.steamApiUrl}/getLatestBuildISO/`;
  getManufacturerUrl = `${environment.apiUrl}/manufacturers`;
  postApplicationUrl = `${environment.steamApiUrl}/applications`;
  getCustomerUrl = `${environment.apiUrl}/getAllDenovoCustomers`;
  constructor(
    private handleError:ErrorHandlerService,
    private httpClient: HttpClient,
    private authService: AuthService,
    private commonService: CommonService
  ) { }

  getAllData(httpReqUrl: string, isFilter?: boolean): Observable<Application[]> {
    // Check if `isFilter` is provided; if not, default to false
    const urlWithParams = isFilter !== undefined ? `${httpReqUrl}?isApplePay=${isFilter}` : httpReqUrl;
  
    return this.httpClient.get<Application>(urlWithParams).pipe(
      map((res: any) => {
        console.log("Result Data: ", res.data);
        return res.data;
      }),
      catchError(this.handleError.handleError)
    ); 
  }
 
  getLastBuildNum(httpReqUrl, parameter) {
    console.log(parameter)
    httpReqUrl = httpReqUrl+parameter.manufacturerId+"/"+parameter.deviceModelId+"/"+parameter.applicationSignatureId;
    return this.httpClient.get(httpReqUrl).pipe(
      map((res: any) => {
        return res.data;
      }),
      catchError(this.handleError.handleError)
    );
  }

  getLastBuildNumISO(httpReqUrl, parameter,data) {
    //console.log(parameter);
    httpReqUrl = httpReqUrl+parameter.manufacturerId+"/"+parameter.deviceModelId+"/"+parameter.applicationSignatureId+`${parameter?.applicationMasterId?'?applicationMasterId='+parameter?.applicationMasterId:''}`;
    return this.httpClient.post(httpReqUrl,data).pipe(
      map((res: any) => {
        return res.data;
      }),
      catchError(this.handleError.handleError)
    );
    
  }
  
  getVirtualTerminalParams(params) {
    let httpParams = new HttpParams();
    Object.keys(params).forEach(function (key) {
      httpParams = httpParams.append(key, params[key]);
    });
    return this.httpClient.get(`${apiLinks.vtPosparam}`, { params: httpParams }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(this.handleError.handleError)
    );
  }

  /** Cloud POS: flag on → payment cache getPosParams; flag off → same as getVirtualTerminalParams (steam). */
  getVirtualTerminalParamsV2(params) {
    const useCache =
      environment.usePosParamCacheApi && params?.callFrom === 'cloudPosPayment';

    if (!useCache) {
      return this.getVirtualTerminalParams(params);
    }

    let httpParams = new HttpParams();
    Object.keys(params || {}).forEach(function (key) {
      httpParams = httpParams.append(key, params[key]);
    });

    return this.httpClient
      .get(apiLinks.getPosParams, {
        params: httpParams,
        headers: new HttpHeaders({
          // Same JWT as payment curl: header `token` with no Bearer prefix
          token: this.authService.getUser.pgAccessToken || '',
        }),
      })
      .pipe(
        map((res: any) => {
          // Cache API may wrap payload as information.data; expose steam shape { message, data }.
          if (res?.data) return res;
          if (res?.information?.data) {
            return { message: res.message, data: res.information.data };
          }
          return res;
        }),
        catchError(this.handleError.handleError)
      );
  }

  createApplication(applicationData) {
    return this.httpClient.post(this.postApplicationUrl, applicationData).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(this.handleError.handleError)
    );
  }

  getReadableBytes(bytes, sizeType) {
    let i = Math.floor(Math.log(bytes) / Math.log(1024)),
    sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let sizesIndex = sizes.indexOf(sizeType);
    if(sizesIndex == -1) {
      return 'Invalid size type';
    }
    return (bytes / Math.pow(1024, i)).toFixed(2) + ' ' + sizes[sizesIndex];
  }

  getAllApplicationData(params) {
    console.log('service', params);
    
    let httpParams = new HttpParams();
    httpParams = httpParams.append('sort', '{"field": "updatedAt", "order": "DESC"}');
    httpParams = httpParams.append('size','20');
    Object.keys(params).forEach(function (key) {
      const value = params[key];
      if (value !== null && value !== undefined && value !== '') {
        httpParams = httpParams.append(key, value);
      }
    })
    return this.httpClient.get(`${environment.steamApiUrl}/applications`, {params: httpParams}).pipe(
      map((res: any) => {
        console.log("Result Data: ", res);
        return res;
      }),
      catchError(this.handleError.handleError)
    ); 
  }

  updateApplicationTag(applicationId: number | string, tag: string) {
    return this.httpClient
      .patch(`${environment.steamApiUrl}/applications/${applicationId}/tag`, { tag })
      .pipe(
        map((res: any) => res),
        catchError(this.handleError.handleError)
      );
  }

  /** Distinct tags from applications (steam). q must be >= 3 chars unless customOnly. */
  getTagSuggestions(query: string, options?: { customOnly?: boolean }) {
    const q = (query || "").trim();
    let params = new HttpParams();
    if (options?.customOnly) {
      params = params.set("customOnly", "true");
    } else {
      params = params.set("q", q);
    }
    return this.httpClient
      .get<{ success?: boolean; data?: string[] }>(
        `${environment.steamApiUrl}/applications/tag-suggestions`,
        { params }
      )
      .pipe(
        map((res: any) => (Array.isArray(res?.data) ? res.data.map((t: any) => (t !== null && t !== undefined) ? String(t).trim() : "").filter(Boolean) : [])),
        catchError(this.handleError.handleError)
      );
  }

  getApplicationHistory(applicationId: number | string) {
    return this.httpClient
      .get(`${environment.steamApiUrl}/applications/${applicationId}/history`)
      .pipe(
        map((res: any) => res),
        catchError(this.handleError.handleError)
      );
  }

  getPGAPI(defaultData, timer, url = apiLinks.processTransaction) {
    // Same code repeated twice only difference is the timer.
    // I am not changing now , but be careful here.
    if(timer == 0) {
      const httpOptions = {
        headers: new HttpHeaders({ 'Authorization': `Bearer ${this.authService.getUser.pgAccessToken}`, 'withCredentials':'false'})
      };
      return this.httpClient.post(`${url}`, defaultData, httpOptions).pipe(
        map((res: any) => {
         return res;
        }),
        catchError(this.handleError.handleError)
      )
    } else {
      const httpOptions = {
        headers: new HttpHeaders({ 'Authorization': `Bearer ${this.authService.getUser.pgAccessToken}`, 'withCredentials':'false', timeout: `${timer*1000}`})
      };
      return this.httpClient.post(`${url}`, defaultData, httpOptions).pipe(
        // timeout(timer * 1000),
        map((res: any) => {
          return res;
        }),
        catchError((error) => {
          return this.handleError.handleError(error)
        })
      )
    }
  }

  getInvoiceDetails(defaultData, url = apiLinks.fetchAndPay.invoiceDetails) {
    const httpOptions = {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${this.authService.getUser.pgAccessToken}`, 'withCredentials':'false'})
    };
    return this.httpClient.post(`${url}`, defaultData, httpOptions).pipe(
      map((res: any) => {
       return res;
      }),
      catchError(this.handleError.handleError)
    )
  }

  getFortellisDetails(defaultData, processType, tpn, integrationType = "1") {
    const httpOptions = {
      headers: new HttpHeaders({ 'token': `Bearer ${this.authService.getUser.pgAccessToken}`, 'integrationType': integrationType,'processType': processType, tpn: tpn })
    };
    return this.httpClient.post(`${apiLinks.getFortellisDetails}`, defaultData, httpOptions).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(this.handleError.handleError)
    )
  }

  createCardinalMerchant(defaultData, processType, tpn) {
    const httpOptions = {
      headers: new HttpHeaders({ 'token': `token`, 'integrationType': '2','processType': processType, tpn: tpn })
    };
    return this.httpClient.post(`${apiLinks.getFortellisDetails}`, defaultData, httpOptions).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(this.handleError.handleError)
    )
  }

  submitLineItems(data, params = {}) {
    return this.httpClient.post(`${apiLinks.quickbooks.submitLineItems}`, data, { params }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(this.handleError.handleError)
    )
  }

  submitInvoiceLineItems(data, params = {}) {
    return this.httpClient.post(`${apiLinks.quickbooks.submitInvoiceLineItems}`, data, { params }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(this.handleError.handleError)
    )
  }
  
  getPaymentForm(data) {
    const httpOptions = {
      headers: new HttpHeaders({ 'token': `Bearer ${this.authService.getUser.pgAccessToken}`, 'withCredentials':'false' })
    }; 
    return this.httpClient.post( `${apiLinks.internalPaymentForm}`, data, httpOptions).pipe(
      map((res: any) => {
       return res;
      }),
      catchError(this.handleError.handleError)
    )
  }

  voidPockytTxn(data) {
    const httpOptions = {
      headers: new HttpHeaders({ 'authorization': this.commonService.getPockytAuth(), 'withCredentials':'false' })
    };    
    return this.httpClient.post(`${apiLinks.pockytRefund}`, data, httpOptions).pipe(
      map((res: any) => {
       return res;
      }, err => {
        return throwError(err);
      }),
    )
  }

  getGhppUrl(defaultData) {
    const httpOptions = {
      headers: new HttpHeaders({ 'Authorization': `api-key cG9ydGFsLVRlbXA9VG9rZW4tcGhhc2UtMQ==`, 'withCredentials':'false'})
    };

    return this.httpClient.post(`${apiLinks.generateGhppUrl}`, defaultData, httpOptions).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(this.handleError.handleError)
    )
  }

  sendEmailorSMS(data) {
    return this.httpClient.post(`${apiLinks.sendEmailorSMS}`, data).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(this.handleError.handleError)
    )
  }
}
